// Odds format for the sportsbook. Prices flow through the app as DECIMAL numbers
// (that's what the gateway/backend return, e.g. 1.91, 2.50). The player picks how
// they want them shown — American (default) or Decimal — and that preference is
// stored per-device in localStorage ("pb_odds_format"). Everything that renders a
// price runs it through formatOdds() so the whole book switches together.

import { useEffect, useState } from "react";

const KEY = "pb_odds_format";
const DEFAULT_FORMAT = "american"; // house standard
export const ODDS_FORMATS = ["american", "decimal"];
// Fired on the window when the preference changes in THIS tab, so open surfaces
// (bet slip, event pages) re-render immediately — the native "storage" event only
// fires in OTHER tabs.
export const ODDS_FORMAT_EVENT = "pb_odds_format_change";

export function getOddsFormat() {
  try {
    const v = localStorage.getItem(KEY);
    return ODDS_FORMATS.includes(v) ? v : DEFAULT_FORMAT;
  } catch {
    return DEFAULT_FORMAT;
  }
}

export function setOddsFormat(format) {
  const v = ODDS_FORMATS.includes(format) ? format : DEFAULT_FORMAT;
  try { localStorage.setItem(KEY, v); } catch { /* private mode */ }
  try { window.dispatchEvent(new Event(ODDS_FORMAT_EVENT)); } catch { /* SSR/no window */ }
  return v;
}

// Decimal price -> American moneyline integer. Even money (2.0) is +100.
//   d >= 2.0 : +round((d - 1) * 100)
//   d <  2.0 : -round(100 / (d - 1))
export function decimalToAmerican(decimal) {
  const d = Number(decimal);
  if (!Number.isFinite(d) || d <= 1) return null;
  return d >= 2 ? Math.round((d - 1) * 100) : -Math.round(100 / (d - 1));
}

// Format a decimal price for display in the given (or current) format.
// Returns "—" for a missing/invalid price so callers don't render "NaN".
export function formatOdds(decimal, format) {
  const d = Number(decimal);
  if (!Number.isFinite(d) || d <= 1) return "—";
  const fmt = format || getOddsFormat();
  if (fmt === "american") {
    const a = decimalToAmerican(d);
    if (a == null) return "—";
    return a > 0 ? `+${a}` : `${a}`;
  }
  return d.toFixed(2);
}

// Reactive current format — re-renders on same-tab changes and cross-tab storage.
export function useOddsFormat() {
  const [fmt, setFmt] = useState(getOddsFormat);
  useEffect(() => {
    const sync = () => setFmt(getOddsFormat());
    window.addEventListener(ODDS_FORMAT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ODDS_FORMAT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return fmt;
}
